/**
 * The tenants the signed-in user can reach.
 *
 * This is what makes the switcher work the same in every app: one fetch of
 * `GET /me/tenants`, in the shell, rather than each app shipping its own tenant
 * selector page. Optimize's `/dashboard`, DMT's picker and Claims Explorer's
 * sidebar were three implementations of one list.
 *
 * Two things it must never do:
 *
 *  1. **Block the host app.** Every failure resolves to an empty list with a
 *     reason. The switcher then falls back to a plain link to Optimize, which is
 *     exactly the behaviour before it existed.
 *  2. **Fetch more than once per page.** A launcher opened three times is one
 *     request. The list changes when someone's membership changes, which is not
 *     a per-click event.
 *
 * ⚠️ Cross-origin reality: the platform CORS allowlist does not contain the app
 * origins (no claims-explorer, no pdm, no DMT — AIE-542), so from a consuming
 * app this fetch fails preflight until that lands. It degrades rather than
 * breaking, and the console says which of the two it was.
 *
 * SAME-ORIGIN HOSTS NEED NO TOKEN
 * A host may point `tenantsUrl` at a path on its own origin — `/api/shell/tenants`
 * — that answers in `/me/tenants` shape. Then the request carries cookies instead
 * of a bearer token, and no token getter is required.
 *
 * That exists because requiring a token excluded a whole class of app. Navigate
 * is server-rendered and keeps its session in an httpOnly cookie: there IS no
 * token in the page for `getToken` to return, and exposing one to JavaScript to
 * satisfy this contract would be a worse design than proxying. Such a host also
 * cannot hit the platform API directly — that needs an access token minted for
 * the platform audience, plus its origin on the allowlist.
 *
 * Deliberately still ONE code path: the shape is the platform's, so a host that
 * proxies today can be pointed at the real endpoint later with nothing else
 * changing.
 */

import type { Org } from "./org"

/** Which environment a tenant represents. Exactly one, unlike payer/provider. */
export type TenantTier = "development" | "test" | "production"

export interface Tenant {
  id: string
  name: string
  tier: TenantTier
  /** How the user is attached. Shown so a member understands why a tenant is listed. */
  role?: string
  isPayer?: boolean
  isProvider?: boolean
  /** The organization's website. The org mark derives a favicon from it, so a
   *  real customer logo appears instead of a monogram. */
  infoUrl?: string
}

/** `production` is the assumed tier when the platform does not send one — the
 *  same safe-direction default the column uses, so an older backend that
 *  predates the field cannot make a real tenant look disposable. */
const DEFAULT_TIER: TenantTier = "production"

const TIERS: readonly TenantTier[] = ["development", "test", "production"]

export const asTier = (value: unknown): TenantTier =>
  TIERS.includes(value as TenantTier) ? (value as TenantTier) : DEFAULT_TIER

/** Display label. Uppercase because that is how it reads as a badge. */
export const tierLabel = (tier: TenantTier): string => tier.toUpperCase()

/**
 * Why the list is empty, when it is.
 *
 * A code rather than only a message, because the switcher needs to tell a person
 * something they can act on. The first version showed one generic sentence for
 * every cause, so "you are not signed in" and "nobody configured this" and "CORS
 * blocked it" were indistinguishable — from the UI *and*, for the common case, from
 * the console.
 */
export type TenantsFailure = "unconfigured" | "no-token" | "http" | "network" | "empty"

export interface TenantsResult {
  tenants: Tenant[]
  error?: string
  reason?: TenantsFailure
}

interface RawTenant {
  id?: string
  name?: string
  tier?: string
  role?: string
  is_payer?: boolean
  is_provider?: boolean
  info_url?: string
}

/**
 * `/me/tenants` answers `{ owner: [...], member: [...] }` — split by how the
 * user is attached, not by anything a switcher cares about. Flatten to one list,
 * because "which of my organizations am I going to" is a single question.
 *
 * Owner entries carry no `role`, so label them; member entries bring their own.
 */
export function normalizeTenants(body: unknown): Tenant[] {
  const shape = (body ?? {}) as { owner?: RawTenant[]; member?: RawTenant[] }
  const groups: [RawTenant[] | undefined, string | undefined][] = [
    [shape.owner, "owner"],
    [shape.member, undefined],
  ]

  const out: Tenant[] = []
  const seen = new Set<string>()

  for (const [list, fallbackRole] of groups) {
    if (!Array.isArray(list)) continue
    for (const raw of list) {
      const id = String(raw?.id ?? "").trim()
      if (!id || seen.has(id)) continue // owner wins: a user can be both
      seen.add(id)
      out.push({
        id,
        name: String(raw?.name ?? "").trim() || id,
        tier: asTier(raw?.tier),
        role: raw?.role || fallbackRole,
        isPayer: raw?.is_payer,
        isProvider: raw?.is_provider,
        infoUrl: String(raw?.info_url ?? "").trim() || undefined,
      })
    }
  }

  // Alphabetical. Deliberately NOT grouped by tier: someone with one production
  // tenant and twelve dev ones should still find it where its name says it is.
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

let inflight: Promise<TenantsResult> | null = null

/** Fetch and cache for the life of the page. Never throws. */
export function loadTenants(
  url: string | null,
  getToken?: () => Promise<string | null>,
): Promise<TenantsResult> {
  if (!url) {
    return Promise.resolve({
      tenants: [],
      reason: "unconfigured",
      error: "no tenants endpoint configured — DexShell.configure({ apiBaseUrl }) or ({ tenantsUrl })",
    })
  }
  inflight ??= fetchTenants(url, getToken)
  return inflight
}

/** Drop the cache — for a sign-in/sign-out, where membership genuinely changed. */
export function forgetTenants(): void {
  inflight = null
}

/**
 * Is this URL on the page's own origin?
 *
 * A relative URL always is. An absolute one is compared by parsed origin rather
 * than by string prefix, so a host that builds `https://its-own-host/api/...` from
 * configuration is not treated differently from one that hardcodes a path.
 *
 * Resolved with `window.location.href` as the base — the same resolution `fetch`
 * itself performs — so this cannot disagree with where the request actually goes.
 * That is the property that matters: a rule that decided "same origin" by string
 * prefix would call `https://navigate.dexcare.health.evil.example/` local, and one
 * that special-cased "starts with /" would miss `//other.example/x`.
 */
export function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

async function fetchTenants(
  url: string,
  getToken?: () => Promise<string | null>,
): Promise<TenantsResult> {
  try {
    const local = isSameOrigin(url)
    const token = getToken ? await getToken() : null
    if (!token && !local) {
      // Not an error worth shouting about: the host may simply not have signed
      // the user in yet. Retryable, so don't poison the cache.
      inflight = null
      return { tenants: [], reason: "no-token", error: "no access token yet" }
    }
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      // A host's own endpoint authenticates the session however it already does —
      // for a server-rendered app that is an httpOnly cookie, which is only sent
      // if we ask for it. `same-origin` and not `include`, because this branch is
      // only taken for a URL we have already proved is local.
      credentials: local ? "same-origin" : "omit",
    })
    if (!res.ok) {
      inflight = null
      return {
        tenants: [],
        reason: "http",
        error: `tenants endpoint responded ${res.status}`,
      }
    }
    const tenants = normalizeTenants(await res.json())
    if (!tenants.length) return { tenants: [], reason: "empty", error: "no tenants returned" }
    return { tenants }
  } catch (e) {
    inflight = null
    const message = e instanceof Error ? e.message : "tenants fetch failed"
    // For a CROSS-origin call this is overwhelmingly the preflight (AIE-542), and
    // saying so matters because "Failed to fetch" sends people looking at auth.
    // For a host's own endpoint it cannot be CORS, and pointing at the allowlist
    // there would send them to fix something that was never wrong.
    const hint = isSameOrigin(url)
      ? " — this is the host's own endpoint, so check that the route exists and returns JSON"
      : " — if this is a cross-origin call, check the platform CORS allowlist (AIE-542)"
    return { tenants: [], reason: "network", error: `${message}${hint}` }
  }
}

/** The tenant currently in context, matched against the fetched list. */
export const findCurrent = (tenants: Tenant[], org: Org | null): Tenant | undefined =>
  org?.id ? tenants.find((t) => t.id === org.id) : undefined

/** Case-insensitive filter for the switcher's search box. Matches name or id, so
 *  pasting a UUID from a bug report finds the tenant. */
export function filterTenants(tenants: Tenant[], query: string): Tenant[] {
  const q = query.trim().toLowerCase()
  if (!q) return tenants
  return tenants.filter(
    (t) => t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q),
  )
}
